import { useEffect, useState } from "react";
import { Flame, TrendingUp, Hash, Wand2, ExternalLink, Heart, MessageCircle, Share2, KeyRound, Clapperboard, Tv , BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";

function SetupNotice() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.veilleStatus().then(setSt).catch(() => {}); }, []);
  if (!st || st.ready) return null;
  return (
    <div className="mb-5 rounded-lg border border-[#e4a11b55] bg-[#fff4d6] px-4 py-3 text-sm flex gap-3" data-testid="veille-setup-notice">
      <KeyRound className="w-4 h-4 mt-0.5 shrink-0 text-[#8a5a00]" />
      <div className="text-[#5c3d00]">
        <b>Veille réelle pas encore activée.</b>{" "}
        {!st.apify_configured && "Clé Apify manquante. "}
        {st.profiles_configured === 0
          ? "Aucun lien Facebook/TikTok de blogueur renseigné."
          : `${st.profiles_configured}/${st.bloggers.length} blogueurs avec liens.`}
      </div>
    </div>
  );
}

const BADGES = {
  comments_fire: { label: "Commentaires en feu", Icon: Flame, cls: "!text-[#c2410c] !bg-[#fff1e6] !border-[#fdba7466]" },
  mass_shares: { label: "Partages massifs", Icon: Share2, cls: "!text-[#1877f2] !bg-[#e7f3ff] !border-[#1877f255]" },
};

function BuzzBadges({ badges, idx }) {
  if (!badges?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`trend-badges-${idx}`}>
      {badges.map(b => BADGES[b] && (
        <span key={b} className={`chip ${BADGES[b].cls}`} data-testid={`badge-${b}-${idx}`}>
          {(() => { const I = BADGES[b].Icon; return <I className="w-3 h-3" />; })()} {BADGES[b].label}
        </span>
      ))}
    </div>
  );
}

function TopComments({ comments, idx }) {
  if (!comments?.length) return null;
  return (
    <div className="mt-3 space-y-1.5" data-testid={`trend-comments-${idx}`}>
      {comments.slice(0, 2).map((c, i) => (
        <div key={i} className="rounded-lg bg-[#f0f2f5] px-3 py-2 text-[12px] leading-snug">
          <span className="line-clamp-2">« {c.text} »</span>
          <span className="mt-0.5 inline-flex items-center gap-1 font-mono-ui text-[10px] text-[color:var(--bcs-text-dim)]">
            <Heart className="w-2.5 h-2.5" /> {c.likes} · un internaute
          </span>
        </div>
      ))}
    </div>
  );
}

function Metrics({ t }) {
  const items = [[Heart, t.likes], [MessageCircle, t.comments], [Share2, t.shares]];
  return (
    <div className="mt-3 flex items-center gap-3 text-[11px] font-mono-ui text-[color:var(--bcs-text-dim)]" data-testid="trend-metrics">
      {t.views > 0 && <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {viewsFmt(t.views)}</span>}
      {items.map(([Icon, v], i) => (
        <span key={i} className="inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {viewsFmt(v || 0)}</span>
      ))}
    </div>
  );
}

function platformChip(source) {
  if (source === "TikTok") return <span className="chip chip-tt">TIKTOK</span>;
  return <span className="chip chip-fb">FACEBOOK</span>;
}

function viewsFmt(v) {
  if (!v && v !== 0) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return `${v}`;
}

function DigestButton({ disabled, onCreated }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.generateDigest();
      toast.success("Résumé du jour lancé : émission de 2-3 min avec les 5 meilleurs buzz. Prêt dans ~4 min dans la galerie.");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={go} disabled={disabled || busy} className="btn-primary shrink-0" data-testid="generate-digest-btn">
      <Tv className="w-4 h-4" /> {busy ? "Lancement…" : "Générer le résumé du jour"}
    </button>
  );
}

function CreateVideoButton({ t, idx, onCreated }) {
  const [busy, setBusy] = useState(null);
  const go = async (model) => {
    setBusy(model);
    try {
      await api.generateVideo({ prompt: t.voiceover || t.title, model, duration_sec: 60, trend_id: t.id, trend_title: t.title });
      toast.success(model === "story" ? "Histoire de 3-4 min lancée : intro, passages clés, réactions, conclusion. Prête dans ~5 min dans la galerie."
        : "Montage lancé : captures + voix off. Prêt dans ~1 min dans la galerie.");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-5 grid grid-cols-2 gap-2">
      <button type="button" onClick={() => go("montage")} disabled={!!busy} className="btn-primary justify-center !px-2 whitespace-nowrap text-[13px]" data-testid={`create-montage-${idx}`}>
        <Clapperboard className="w-4 h-4" /> {busy === "montage" ? "Lancement…" : "Vidéo courte"}
      </button>
      <button type="button" onClick={() => go("story")} disabled={!!busy} className="btn-sharp justify-center !px-2 whitespace-nowrap text-[13px]" data-testid={`create-story-${idx}`}>
        <BookOpen className="w-4 h-4" /> {busy === "story" ? "Lancement…" : "Histoire 3-4 min"}
      </button>
    </div>
  );
}

export default function TrendCards({ trends, loading, onUsePrompt, onCreated }) {
  return (
    <section className="card-surface p-6" data-testid="trends-today-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="w-full sm:w-auto sm:flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Tendances du jour</h2>
          <p className="text-sm text-[color:var(--bcs-text-mut)] mt-1">
            Tous les buzz du jour (jusqu'à 3 par source) · 10 dernières publications par profil · commentaires & partages pris en compte · 48 h
          </p>
        </div>
        <DigestButton disabled={!trends || trends.length < 2} onCreated={onCreated} />
      </div>

      <SetupNotice />

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-56 rounded-xl bg-[#f7f8fa] animate-pulse" />
          ))}
        </div>
      )}

      {!loading && (!trends || trends.length === 0) && (
        <div className="py-16 text-center text-[color:var(--bcs-text-mut)]">
          <div className="font-mono-ui text-xs tracking-widest uppercase mb-3">AUCUNE VEILLE EXÉCUTÉE</div>
          <p className="text-sm">Lancez une veille pour récupérer les publications qui buzzent le plus chez vos blogueurs.</p>
        </div>
      )}

      {!loading && trends && trends.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {trends.map((t, idx) => (
            <article
              key={t.id}
              className="relative overflow-hidden rounded-xl border border-[#dddfe2]/80 bg-gradient-to-b from-[#ffffff] to-[#ffffff] p-5 fade-up"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`trend-card-${idx}`}
            >
              {t.captures?.[0] && (
                <img src={mediaUrl(`/api/media/${t.captures[0]}`)} alt={t.creator_handle}
                  className="-mx-5 -mt-5 mb-4 w-[calc(100%+2.5rem)] max-w-none h-44 object-cover object-top"
                  loading="lazy" data-testid={`trend-capture-${idx}`} />
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {platformChip(t.source)}
                  <span className="font-mono-ui text-[10.5px] text-[color:var(--bcs-text-dim)]">
                    {t.creator_handle}
                  </span>
                </div>
                <div className="relative shrink-0">
                  <div
                    className="ring-score w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ "--score": t.viral_score }}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#f0f2f5] flex items-center justify-center">
                      <span className="font-mono-ui text-[12px] font-bold text-[color:var(--bcs-accent)]">
                        {t.viral_score}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="font-display text-lg font-semibold leading-snug mt-4">{t.title}</h3>
              <p className="text-sm text-[color:var(--bcs-text-mut)] mt-2 line-clamp-3">{t.summary}</p>

              <Metrics t={t} />
              <BuzzBadges badges={t.badges} idx={idx} />
              <TopComments comments={t.top_comments} idx={idx} />
              <p className="mt-2 text-[12px] italic text-[color:var(--bcs-text-mut)] truncate">"{t.hook}"</p>
              {t.source_url && (
                <a href={t.source_url} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--bcs-accent)] hover:underline"
                  data-testid={`trend-source-link-${idx}`}>
                  <ExternalLink className="w-3 h-3" /> Voir la publication originale
                </a>
              )}

              {t.hashtags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.hashtags.slice(0, 4).map(h => (
                    <span
                      key={h}
                      className="inline-flex items-center gap-1 text-[10.5px] font-mono-ui text-[color:var(--bcs-accent-2)] bg-[#fff4d6] border border-[#e4a11b55] rounded-md px-1.5 py-0.5"
                    >
                      <Hash className="w-2.5 h-2.5" /> {h}
                    </span>
                  ))}
                </div>
              )}

              <CreateVideoButton t={t} idx={idx} onCreated={onCreated} />
              <button
                type="button"
                onClick={() => onUsePrompt?.(t)}
                className="btn-sharp mt-2 w-full justify-center"
                data-testid={`use-prompt-${idx}`}
              >
                <Wand2 className="w-3.5 h-3.5" />
                Modifier la voix off
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
